import React, { createContext, useContext, useRef, useState, useCallback, useEffect, ReactNode } from 'react';

export interface VoicemailAudioState {
  currentVoicemailId: string | null;
  playing: boolean;
  currentTime: number;
  duration: number;
  audioSrc: string;
  audioSrcLoader: boolean;
}

export interface VoicemailAudioActions {
  playVoicemail: (voicemailId: string, audioSrc: string, audioSrcLoader: boolean) => void;
  resumeVoicemail: () => void;
  pauseVoicemail: () => void;
  setCurrentTime: (time: number) => void;
  stopVoicemail: () => void;
  isCurrentVoicemail: (voicemailId: string) => boolean;
}

export interface VoicemailAudioContextType extends VoicemailAudioState, VoicemailAudioActions {}

const VoicemailAudioContext = createContext<VoicemailAudioContextType | null>(null);

export interface VoicemailAudioProviderProps {
  children: ReactNode;
}

export const VoicemailAudioProvider: React.FC<VoicemailAudioProviderProps> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<VoicemailAudioState>({
    currentVoicemailId: null,
    playing: false,
    currentTime: 0,
    duration: 0,
    audioSrc: '',
    audioSrcLoader: false,
  });

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'auto';
      audioRef.current.crossOrigin = 'anonymous';
    }

    const audio = audioRef.current;

    const handleLoadedData = () => {
      setState(prev => ({
        ...prev,
        duration: audio.duration,
        currentTime: audio.currentTime,
      }));
    };

    const handleTimeUpdate = () => {
      setState(prev => ({
        ...prev,
        currentTime: audio.currentTime,
      }));
    };

    const handleEnded = () => {
      setState(prev => ({
        ...prev,
        playing: false,
        currentTime: 0,
      }));
    };

    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Effect that reacts to state.playing / state.audioSrc to keep the element in sync
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!state.audioSrc) {
      audio.pause();
      return;
    }

    if (state.playing && audio.paused) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(err => {
          // eslint-disable-next-line no-console
          console.warn('Audio play (effect) failed, reverting playing state', err);
          setState(prev => ({ ...prev, playing: false }));
        });
      }
    }

    if (!state.playing && !audio.paused) {
      audio.pause();
    }
  }, [state.playing, state.audioSrc]);

  const playVoicemail = useCallback((voicemailId: string, audioSrc: string, audioSrcLoader: boolean) => {
    const audio = audioRef.current;
    if (!audio) return;

    const hasSrc = !!audioSrc;
    const switching = voicemailId !== state.currentVoicemailId || (hasSrc && audio.src !== audioSrc);

    if (switching) {
      // Reset previous playback position when switching items
      audio.pause();
      audio.currentTime = 0;
      if (hasSrc && audio.src !== audioSrc) {
        audio.src = audioSrc;
      }
    }

    setState(prev => ({
      ...prev,
      currentVoicemailId: voicemailId,
      audioSrc,
      audioSrcLoader,
      playing: hasSrc,
    }));

    if (hasSrc) {
      const playResult = audio.play();
      if (playResult && typeof playResult.then === 'function') {
        playResult.catch(err => {
          // eslint-disable-next-line no-console
          console.warn('Immediate play attempt failed', err);
          setState(prev => ({ ...prev, playing: false }));
        });
      }
    } else {
      audio.pause();
      setState(prev => ({ ...prev, playing: false }));
    }
  }, []);

  const pauseVoicemail = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    setState(prev => ({ ...prev, playing: false }));
  }, []);

  const resumeVoicemail = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Only resume if we have a current voicemail and audio source
    if (state.currentVoicemailId && state.audioSrc) {
      setState(prev => ({ ...prev, playing: true }));
      audio.play().catch(console.error);
    }
  }, [state.currentVoicemailId, state.audioSrc]);

  const setCurrentTime = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = time;
    setState(prev => ({ ...prev, currentTime: time }));
  }, []);

  const stopVoicemail = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    audio.src = '';
    setState({
      currentVoicemailId: null,
      playing: false,
      currentTime: 0,
      duration: 0,
      audioSrc: '',
      audioSrcLoader: false,
    });
  }, []);

  const isCurrentVoicemail = useCallback((voicemailId: string) => {
    return state.currentVoicemailId === voicemailId;
  }, [state.currentVoicemailId]);

  const value: VoicemailAudioContextType = {
    ...state,
    playVoicemail,
    resumeVoicemail,
    pauseVoicemail,
    setCurrentTime,
    stopVoicemail,
    isCurrentVoicemail,
  };

  return (
    <VoicemailAudioContext.Provider value={value}>
      {children}
    </VoicemailAudioContext.Provider>
  );
};

export const useVoicemailAudio = (): VoicemailAudioContextType => {
  const context = useContext(VoicemailAudioContext);
  if (!context) {
    throw new Error('useVoicemailAudio must be used within a VoicemailAudioProvider');
  }
  return context;
};